
import { es } from 'date-fns/locale/es';
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import SelectReact from "react-select";
import { InputProps } from "rizzui";
import cn from "../../utils/classNames";
import { DatePicker } from "./DatePicker";

export interface DatePickerProps<selectsRange extends boolean | undefined>
    extends Omit<any, "selectsRange" | "onChange"> {
    onChange(
        date: selectsRange extends false | undefined ? Date | null : [Date | null, Date | null],
        event: React.SyntheticEvent<any> | undefined
    ): void;
    selectsRange?: selectsRange;
    inputProps?: InputProps;
    wrapperClassName?: string;
}

const datesAreEqual = (left: Date | null | undefined, right: Date | null | undefined) => {
    if (!left && !right) return true;
    if (!left || !right) return false;
    return left.getTime() === right.getTime();
};

export default function DateField({
    onClear,
    placeholderText = 'Seleciona Fecha',
    inputProps,
    label = null,
    dateTypesOptions,
    wrapperClassName,
    months = 1,
    className = "w-72",
    ...props
}: any & { onClear?: () => void }) {
    const [selectedDateField, setSelectedDateField] = useState<any>(props.selectedDateField);
    const [between_dates, setBetweenDates] = useState<any>({ startDate: props.startDate, endDate: props.endDate });
    const skipParentNotifyRef = useRef(true);

    useEffect(() => {
        const nextStart = props.startDate ?? null;
        const nextEnd = props.endDate ?? null;

        setBetweenDates((prev: any) => {
            if (datesAreEqual(prev.startDate, nextStart) && datesAreEqual(prev.endDate, nextEnd)) {
                return prev;
            }

            skipParentNotifyRef.current = true;
            return { startDate: nextStart, endDate: nextEnd };
        });
    }, [props.startDate, props.endDate]);

    useEffect(() => {
        if (skipParentNotifyRef.current) {
            skipParentNotifyRef.current = false;
            return;
        }

        if (between_dates.startDate && between_dates.endDate) {
            props.onChange([
                moment(between_dates.startDate).format('YYYY-MM-DD'),
                moment(between_dates.endDate).format('YYYY-MM-DD'),
                selectedDateField,
            ]);
        } else if (!between_dates.startDate && !between_dates.endDate) {
            props.onChange(null);
        }
    }, [between_dates, selectedDateField]);

    return (
        <div className={cn(wrapperClassName)}>
            {dateTypesOptions ?
                <>
                    <span className="rizzui-input-label block text-sm mb-1.5 font-medium">Rango de fecha</span>
                    <SelectReact
                        classNamePrefix="attiva-select"
                        options={dateTypesOptions}
                        onChange={(option: any) => setSelectedDateField(option.value)}
                        value={dateTypesOptions.find((option: any) => option.value === selectedDateField)}
                        className="mb-4"
                        placeholder={`Elige el tipo...`}
                        styles={{
                            control: (base: any, state: any) => ({
                                ...base,
                                border: '1px !important',
                                borderRadius: '0.375rem',
                                borderColor: 'rgb(229 231 235) !important',
                                boxShadow: state.isFocused ? '0 0 0 2px rgb(0, 172, 216)' : '0 0 0 1px rgba(0,0,0, 0.2) !important',
                            }),
                            option: (provided: any, state: any) => ({
                                ...provided,
                                backgroundColor: state.isFocused ? 'rgb(0, 172, 216)' : 'white',
                                color: state.isFocused ? 'white' : 'black',
                                '&:hover': {
                                    backgroundColor: 'rgb(0, 172, 216)',
                                    color: 'white',
                                    borderColor: '#000000 !important'
                                }
                            }),
                        }}
                    />
                </>
                :
                <>
                    {label && (
                        <span className="rizzui-input-label block text-sm mb-1.5 font-medium">{label}</span>
                    )}
                </>
            }



            <DatePicker
                monthsShown={months}
                placeholderText={placeholderText}
                selectsRange
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                yearDropdownItemNumber={15}
                scrollableYearDropdown
                inputProps={{
                    inputClassName: 'h-9 [&_input]:text-ellipsis',
                    ...inputProps,
                }}
                className={className}
                {...props}
                startDate={between_dates.startDate}
                endDate={between_dates.endDate}
                onChange={(date: any) => {
                    setBetweenDates({
                        startDate: date[0] ? date[0] : null,
                        endDate: date[1] ? date[1] : null,
                    });
                }}
                onClear={() => { setBetweenDates({ startDate: null, endDate: null }); }}
                locale={es}
                popperPlacement="bottom-start"
            />
        </div>
    );
}
